import React, { useContext } from 'react';
import { MainContext } from '../../context/MainContext';

import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';

import WifiIcon from '@mui/icons-material/Wifi';
import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore';
import MemoryIcon from '@mui/icons-material/Memory';
import BluetoothIcon from '@mui/icons-material/Bluetooth';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import RouterIcon from '@mui/icons-material/Router';
import SignalWifiBadIcon from '@mui/icons-material/SignalWifiBad';
import LanIcon from '@mui/icons-material/Lan';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CloudOutlinedIcon from '@mui/icons-material/CloudOutlined';
import DnsOutlinedIcon from '@mui/icons-material/DnsOutlined';
import HourglassEmptyOutlinedIcon from '@mui/icons-material/HourglassEmptyOutlined';
import TimerOffOutlinedIcon from '@mui/icons-material/TimerOffOutlined';
import RemoveOutlinedIcon from '@mui/icons-material/RemoveOutlined';
import StorageOutlinedIcon from '@mui/icons-material/StorageOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import BrokenImageOutlinedIcon from '@mui/icons-material/BrokenImageOutlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';

import * as Constants from '../api/constants';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';


export default function HistorySNWifiRF() {
  const state = useContext(MainContext).state;
  const sn = state.snWifiRf.split(',');
  const deviceId = state.selectedDevice;

  const ICON_COLORS = {
    heat: "#FF7043",        // orange / heat
    cool: "#4FC3F7",        // light blue
    water: "#29B6F6",       // pool / water
    time: "#BA68C8",        // timers
    power: "#FFD54F",       // electrical
    speed: "#81C784",       // motion / speed
    level: "#90A4AE",       // neutral / measurement
    error: "#FF0000",
    bluetooth: "#0a3d91",
    wifi: "#D3D3D3",
    mqtt: "#660066",
    uartTimeout: "#FB8C00",       // orange
    uartBreak: "#8E24AA",         // purple
    uartBuffer: "#00897B",        // teal
    uartOverflow: "#E53935",      // red
    uartFrameError: "#5E35B1",    // deep purple
    uartParity: "#3949AB"         // indigo
  };

  // Icon mapping for each row
  const ICONS = [
    <SettingsBackupRestoreIcon sx={{ color: ICON_COLORS.level }} />,  // Command Echo
    <MemoryIcon sx={{ color: ICON_COLORS.speed }} />,                 // Software Version
    <BluetoothIcon sx={{ color: ICON_COLORS.bluetooth }} />,              // BLE MAC
    <WifiIcon sx={{ color: ICON_COLORS.wifi }} />,                   // Wifi RSSI
    <AccessTimeIcon sx={{ color: ICON_COLORS.time }} />,             // Up time total
    <AccessTimeIcon sx={{ color: ICON_COLORS.time }} />,             // Up time wifi
    <AccessTimeIcon sx={{ color: ICON_COLORS.time }} />,             // Up time BLE
    <AccessTimeIcon sx={{ color: ICON_COLORS.time }} />,             // Up time TCP
    <RouterIcon sx={{ color: ICON_COLORS.level }} />,                 // WiFi SSID
    <LanIcon sx={{ color: ICON_COLORS.wifi }} />,                    // BSSID enable
    <LanIcon sx={{ color: ICON_COLORS.wifi }} />,                    // BSSID MAC
    <SignalWifiBadIcon sx={{ color: ICON_COLORS.wifi }} />,          // Wifi fail
    <DnsOutlinedIcon sx={{ color: ICON_COLORS.level }} />,            // TCP fail
    <ErrorOutlineIcon sx={{ color: ICON_COLORS.power }} />,           // Server RF ping fail
    <WifiIcon sx={{ color: ICON_COLORS.wifi }} />,                   // Wifi MAC
    <CloudOutlinedIcon sx={{ color: ICON_COLORS.mqtt }} />,          // MQTT Server
    <CloudOutlinedIcon sx={{ color: ICON_COLORS.mqtt }} />,          // MQTT Status
    <HourglassEmptyOutlinedIcon sx={{ color: ICON_COLORS.uartTimeout }} />, // UART consecutive timeouts
    <TimerOffOutlinedIcon sx={{ color: ICON_COLORS.uartTimeout }} />,        // UART total timeouts
    <RemoveOutlinedIcon sx={{ color: ICON_COLORS.uartBreak }} />,            // UART break events
    <StorageOutlinedIcon sx={{ color: ICON_COLORS.uartBuffer }} />,          // UART RX buffer full events
    <WarningAmberOutlinedIcon sx={{ color: ICON_COLORS.uartOverflow }} />,   // UART FIFO overflow events
    <BrokenImageOutlinedIcon sx={{ color: ICON_COLORS.uartFrameError }} />,  // UART RX frame error events
    <ErrorOutlineOutlinedIcon sx={{ color: ICON_COLORS.uartParity }} />,     // UART RX parity events
  ];

  const rows = [
    { id: 0, label: 'Command Echo' },
    { id: 1, label: 'Software Version' },
    { id: 2, label: 'BLE MAC Address' },
    { id: 3, label: 'Wi-Fi RSSI' },
    { id: 4, label: 'Up time: Total' },
    { id: 5, label: 'Up time: Wi-Fi' },
    { id: 6, label: 'Up time: BLE' },
    { id: 7, label: 'Up time: TCP' },
    { id: 8, label: 'Wi-Fi SSID' },
    { id: 9, label: 'BSSID enable' },
    { id: 10, label: 'BSSID MAC Address' },
    { id: 11, label: 'Wi-Fi Fail Counter' },
    { id: 12, label: 'TCP Fail Counter' },
    { id: 13, label: 'RF Ping Fail Counter' },
    { id: 14, label: 'Wi-Fi MAC Address' },
    { id: 15, label: 'MQTT Server Address' },
    { id: 16, label: 'MQTT Status' },
    { id: 17, label: 'UART Consecutive Timeouts' },
    { id: 18, label: 'UART Total Timeouts' },
    { id: 19, label: 'UART Break Events' },
    { id: 20, label: 'UART RX Buffer Full Events' },
    { id: 21, label: 'UART FIFO Overflow Events' },
    { id: 22, label: 'UART RX Frame Error Events' },
    { id: 23, label: 'UART RX Parity Events' },
  ];

  // Fill values
  rows.forEach((row, index) => {
    row.value = sn[index] ?? 'N/A';
  });

  const sendCommand = (command, value) => {
    const requestOptions = {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        DeviceId: deviceId,
        Command: command,
        Value: value,
      }),
      credentials: "include"
    };

    fetch(Constants.baseURL + Constants.apiSendCommand, requestOptions)
      .then((response) => {
        if (response.ok) {
          toast.success('Updated successfully...', {
            position: "top-right",
            autoClose: 7000,
          });

          setTimeout(() => state.refreshDeviceRF(deviceId), 3000);
          setTimeout(() => state.getUserDeviceRf(deviceId), 9000);
        } else {
          toast.error('Update failed', { autoClose: 4000 });
        }
      }).catch(error => console.log('request failed', error));
  }

  return (
    <>
      <ToastContainer />

      <Typography
        component="h2"
        variant="h6"
        sx={{ color: "white", fontWeight: 600, mb: 2 }}
      >
        SN-WIFI-RF Data
      </Typography>

      <Grid container spacing={1.5}>
        {rows.map((item, index) => (
          <Grid item xs={12} md={6} key={item.id}>
            <Paper
              sx={{
                p: 1.8,
                borderRadius: 2,
                bgcolor: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.08)",
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                transition: "0.2s ease",
                minHeight: 100,
                "&:hover": {
                  bgcolor: "rgba(255,255,255,0.08)",
                  transform: "translateY(-2px)",
                },
              }}
            >
              {/* Icon bubble */}
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "rgba(255,255,255,0.12)",
                  color: "white",
                }}
              >
                {ICONS[index]}
              </Box>

              {/* Label and Value Chip */}
              <Box sx={{
                flexGrow: 1,
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                gap: 0.3,
                mt: 0.4
              }}>
                <Typography
                  sx={{
                    color: "white",
                    fontSize: 14,
                    fontWeight: 700,
                    lineHeight: 1.1,
                  }}
                >
                  {item.label}
                </Typography>

                <Typography
                  variant="caption"
                  sx={{
                    fontSize: 13,
                    color: 'text.disabled',
                  }}
                >
                  {item.value ? item.value : '-'}
                </Typography>
              </Box>
            </Paper>
          </Grid>
        ))}

        {/* Update button tile */}
        <Grid item xs={12}>
          <Paper
            sx={{
              p: 2,
              textAlign: "center",
              borderRadius: 2,
              bgcolor: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.08)",
            }}
          >
            <Button
              variant="outlined"
              onClick={() => sendCommand("SN-WIFI-RF", "")}
              sx={{
                color: "white",
                borderColor: "rgba(255,255,255,0.4)",
                px: 4,
                textTransform: "none",
                "&:hover": {
                  borderColor: "white",
                  bgcolor: "rgba(255,255,255,0.08)",
                },
              }}
            >
              Update
            </Button>
          </Paper>
        </Grid>
      </Grid>
    </>
  );
}
